'use client'

import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { InfoIcon } from 'lucide-react'
import type { IntegrationProvider } from '@/lib/integrations-constants'
import { parsePixelIds, pixelIdListErrorMessage } from '@/lib/tracking/pixel-id'

// ============================================================================
// Types & Schema
// ============================================================================

const pixelTrackingSchema = z.object({
  // Multi-value field: merchants separate several IDs with `;` (see the notice
  // rendered below). Validated as a LIST (TAG-1871) — this is the field the PX1
  // Research merchant pasted a whole TikTok <script> into, which the funnel
  // editor then split on every comma into 56 junk pixels.
  pixelId: z.string().superRefine((value, ctx) => {
    const message = pixelIdListErrorMessage(value)
    if (message) ctx.addIssue({ code: z.ZodIssueCode.custom, message })
  }),
  apiToken: z.string().optional(),
  trackPageView: z.boolean(),
  trackInitiateCheckout: z.boolean(),
  trackPurchase: z.boolean(),
  trackAddToCart: z.boolean(),
  trackViewContent: z.boolean(),
})

type FormValues = z.infer<typeof pixelTrackingSchema>

interface PixelTrackingFormProps {
  provider: IntegrationProvider
  defaultValues?: Record<string, any>
  onSubmit: (data: Record<string, any>) => Promise<void>
  isLoading?: boolean
}

// ============================================================================
// Component
// ============================================================================

export function PixelTrackingForm({
  provider,
  defaultValues = {},
  onSubmit,
  isLoading = false,
}: PixelTrackingFormProps) {
  const form = useForm<FormValues>({
    resolver: zodResolver(pixelTrackingSchema),
    defaultValues: {
      pixelId: defaultValues.settings?.pixelId ?? '',
      apiToken: defaultValues.credentials?.apiToken ?? '',
      trackPageView: defaultValues.settings?.events?.PageView ?? true,
      trackInitiateCheckout: defaultValues.settings?.events?.InitiateCheckout ?? true,
      trackPurchase: defaultValues.settings?.events?.Purchase ?? true,
      trackAddToCart: defaultValues.settings?.events?.AddToCart ?? true,
      trackViewContent: defaultValues.settings?.events?.ViewContent ?? true,
    },
  })

  const { register, handleSubmit, formState: { errors }, watch, setValue } = form

  const handleFormSubmit = async (data: FormValues) => {
    const { pixelId, apiToken, trackPageView, trackInitiateCheckout, trackPurchase, trackAddToCart, trackViewContent } = data

    // Persist the canonical, vetted list rather than the raw field, so
    // `settings.pixelId` can never hold anything the storefront would choke on.
    const { ids } = parsePixelIds(pixelId)

    await onSubmit({
      settings: {
        pixelId: ids.join(';'),
        events: {
          ...defaultValues.settings?.events,
          PageView: trackPageView,
          InitiateCheckout: trackInitiateCheckout,
          Purchase: trackPurchase,
          AddToCart: trackAddToCart,
          ViewContent: trackViewContent,
        },
      },
      credentials: apiToken ? { apiToken } : {},
    })
  }

  // Configuration based on provider
  const getProviderConfig = () => {
    switch (provider) {
      case 'facebook':
        return {
          name: 'Facebook Pixel',
          pixelIdPlaceholder: '123456789012345',
          pixelIdDescription: 'Your Facebook Pixel ID is a 15-16 digit number',
          multipleInfo: 'You can add multiple pixel IDs by separating them with semicolons (;)',
          findPixelIdSteps: [
            'Go to Facebook Events Manager',
            'Select your pixel from the Data Sources tab',
            'Click on "Settings"',
            'Your Pixel ID will be displayed at the top',
          ],
        }
      case 'tiktok':
        return {
          name: 'TikTok Pixel',
          pixelIdPlaceholder: 'ABCDEFGHIJ1234567890',
          pixelIdDescription: 'Your TikTok Pixel ID is a string of letters and numbers',
          multipleInfo: 'You can add multiple pixel IDs by separating them with semicolons (;)',
          findPixelIdSteps: [
            'Go to TikTok Ads Manager',
            'Navigate to "Assets" → "Events"',
            'Select your pixel',
            'Your Pixel ID will be shown in the pixel details',
          ],
        }
      default:
        return {
          name: 'Pixel',
          pixelIdPlaceholder: 'Enter your pixel ID',
          pixelIdDescription: 'Enter your tracking pixel ID',
          multipleInfo: null,
          findPixelIdSteps: [],
        }
    }
  }

  const config = getProviderConfig()
  const EVENTS = [
    { key: 'trackPageView', label: 'Page View' },
    { key: 'trackInitiateCheckout', label: 'Initiate Checkout' },
    { key: 'trackPurchase', label: 'Purchase' },
    { key: 'trackAddToCart', label: 'Add to Cart' },
    { key: 'trackViewContent', label: 'View Content' },
  ] as const

  return (
    <form id="integration-config-form" onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      {/* Multiple Pixel Info */}
      {config.multipleInfo && (
        <Alert>
          <InfoIcon className="h-4 w-4" />
          <AlertTitle>Multiple Pixel Support</AlertTitle>
          <AlertDescription>
            {config.multipleInfo}
          </AlertDescription>
        </Alert>
      )}

      {/* Pixel Configuration */}
      <Card>
        <CardHeader>
          <CardTitle>Pixel Configuration</CardTitle>
          <CardDescription>Enter your {config.name} ID</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="pixelId">
              Pixel ID
              <span className="ml-1 text-destructive">*</span>
            </Label>
            <Input
              id="pixelId"
              placeholder={config.pixelIdPlaceholder}
              disabled={isLoading}
              {...register('pixelId')}
            />
            <p className="text-xs text-muted-foreground">
              {config.pixelIdDescription}
            </p>
            {errors.pixelId && (
              <p className="text-xs text-destructive">{errors.pixelId.message}</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Event Tracking */}
      <Card>
        <CardHeader>
          <CardTitle>Event Tracking</CardTitle>
          <CardDescription>Select which events to track with this pixel</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {EVENTS.map((event) => (
              <div key={event.key} className="flex items-center space-x-2">
                <Checkbox
                  id={event.key}
                  checked={watch(event.key)}
                  onCheckedChange={(checked) => setValue(event.key, checked as boolean)}
                  disabled={isLoading}
                />
                <Label htmlFor={event.key} className="cursor-pointer font-normal">
                  {event.label}
                </Label>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Setup Instructions */}
      {config.findPixelIdSteps.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Setup Instructions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="rounded-lg bg-muted p-4">
              <p className="mb-3 text-sm font-medium text-muted-foreground">
                Where to find your Pixel ID:
              </p>
              <ol className="list-inside list-decimal space-y-2 text-sm text-muted-foreground">
                {config.findPixelIdSteps.map((step, index) => (
                  <li key={index}>{step}</li>
                ))}
              </ol>
            </div>
          </CardContent>
        </Card>
      )}
    </form>
  )
}
